import { ITooltip } from '@components';
import { ResourcesItemMenu } from '@components/AssetPageMenus/ResourcesItemMenu/ResourcesItemMenu';
import { ResourcesMenu } from '@components/AssetPageMenus/ResourcesMenu/ResourcesMenu';
import { ResourceLink } from '@components/ResourceLink/ResourceLink';
import { handleClick } from '@components/ResourceLink/ResourceLink.utils';
import { useState as useHookState } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { AssetContext } from '@pages/Asset/Asset.query';
import { changeResource } from '@services/queries/resources';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { getPermissionData, getResourceSource } from '@utils';
import { useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Search } from '../Search/Search';
import {
  makeShortcutRefCallback,
  shortcuthashes,
} from '../Sidebar/components/Shortcuts/Shortcuts.utils';
import { NoFeatureMenu } from '../Updates/Components/NoFeatureMenu/NoFeatureMenu';
import { AddResource } from './Components/AddResource/AddResource';
import { EditResource } from './Components/EditResource/EditResource';
import { NoResources } from './Components/NoResources/NoResources';
import { CountLoading, ResourcesLoading } from './Resources.loading';
import {
  Container,
  Counter,
  CounterWrapper,
  Head,
  Items,
  Row,
  Title,
} from './Resources.styles';

export const Resources = () => {
  const search = useHookState<string>('');

  const {
    resourcesQuery,
    sortResourceByFavorite,
    openEditResource,
    openAddResource,
    showResources,
    shortcutRefs,
  } = useContext(AssetContext);
  const { role } = useContext(NavigationContext);

  const params = useParams<{ equipmentId: string }>();
  const queryClient = useQueryClient();

  const changeMutation = useMutation(changeResource, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'resources',
      ]);
    },
  });

  const favoritePermission = getPermissionData({
    owner: true,
    action: 'favorite',
    role,
    type: 'Resource',
  });

  const { isDesktop, isMobile } = useSelector(checkDevice());
  if (
    !resourcesQuery ||
    !resourcesQuery.data ||
    resourcesQuery.isLoading ||
    resourcesQuery.isError
  ) {
    return <ResourcesLoading />;
  }

  const containerState = resourcesQuery.data.length === 0 ? 'empty' : '';

  const count = () => {
    return resourcesQuery?.data?.length || 0;
  };

  const isCountLoading = () => {
    return resourcesQuery?.isLoading;
  };

  const refCallback = makeShortcutRefCallback(
    shortcutRefs,
    shortcuthashes.resources,
  );
  return (
    <Container data-state={containerState} ref={refCallback}>
      <Head>
        <CounterWrapper>
          <Title>Resources</Title>
          {isCountLoading() ? <CountLoading /> : <Counter>{count()}</Counter>}
        </CounterWrapper>

        <Row className="icons">
          {isDesktop && (
            <Search loadSearch={search.set} helperText="Find a resource" />
          )}
          {isDesktop && <ResourcesMenu />}
          {isMobile && <NoFeatureMenu />}
        </Row>
      </Head>

      <Items hidden={containerState === 'empty'}>
        {resourcesQuery.data
          .filter(item =>
            item.label?.toLowerCase().includes(search.get().toLowerCase()),
          )
          .sort((a, b) => {
            if (sortResourceByFavorite.get()) {
              return Number(b.favorite) - Number(a.favorite);
            }
            return 0;
          })
          .map(resource => (
            <ResourceLink
              key={resource.id}
              data-for="resource-link-tip"
              onStarClick={handleClick(
                favoritePermission,
                changeMutation,
                resource,
              )}
              isFavorite={resource.favorite}
              showSourceOrDesc={showResources.get()}
              label={resource.label}
              uri={resource.uri}
              sourceOrDesc={getResourceSource(resource)}
              menu={isDesktop && <ResourcesItemMenu resource={resource} />}
            />
          ))}
        <ITooltip id="resource-link-tip" place="bottom" />
      </Items>
      {containerState === 'empty' && <NoResources />}

      {openEditResource.get() && <EditResource />}

      {openAddResource.get() && <AddResource />}
    </Container>
  );
};
